import { Api } from '@/Api';
import { defineStore } from 'pinia';

import { useViewerItemStore } from './useViewerItemStore';
import { useToast } from '@/composables/useToast';
import { useDebug } from '@/composables/useDebug';
import { useUserStore } from '@/store/useUserStore';

const { debug } = useDebug();

export interface Comment {
  id: string;
  type: string;
  createdAt: number;
  updatedAt: number;
  content: string;
  anonymous: boolean;
  createdAtS: string;
  updatedAtS: string;
  commentHtml: string;
  editable: boolean;
  commentable: boolean;
  author: string;
  authorAvatar: string;
  // not used for now
  // relationships: {
  //   mentions: {
  //     data: [];
  //   };
  // };
  // links: {
  //   cloudapp_links: [];
  // };
}

export interface CommentMention {
  name: string;
  id: string;
  email: string;
}

export interface CommentState {
  isLoading: boolean;
  list: Comment[];
  mentionLoaded: CommentMention[];
}

export const useCommentsStore = defineStore('commentsStore', {
  state: (): CommentState => ({
    isLoading: false,
    list: [],
    mentionLoaded: [],
  }),
  actions: {
    parseCommentMessage(comment: any) {
      return {
        id: comment.id,
        type: comment.type,
        ...comment.attributes,
      };
    },
    async load(itemId: string) {
      const { error } = useToast();
      try {
        this.isLoading = true;
        const { data } = await Api.item.comments.get('Item', itemId || '');

        this.isLoading = false;
        this.list = data.comments?.map(this.parseCommentMessage) || [];
      } catch (err) {
        this.isLoading = false;
        debug(err);
        error('Cannot load comments list');
      }
    },
    async add(content: string, displayName: string | undefined = undefined) {
      const viewerItemStore = useViewerItemStore();
      const itemId = viewerItemStore.item?.id;
      const { error } = useToast();
      const mentions = this.getMentionsFromMessage(content);

      try {
        const response = await Api.item.comments.post({
          commentableId: itemId,
          commentableType: 'Item',
          content,
          displayName,
          mentions,
        });

        this.list.push(this.parseCommentMessage(response.data.comment));

        return Promise.resolve();
      } catch (err: any) {
        debug(err);
        error('There was an error while adding a comment');

        return Promise.reject(err);
      }
    },
    async update(id: string, content: string) {
      const { error } = useToast();

      const mentions = this.getMentionsFromMessage(content);

      try {
        const response = await Api.item.comments.update(id, { content, mentions });
        const comment = this.parseCommentMessage(response.data.comment);

        this.list = this.list.map(c => {
          if (c.id === comment.id) {
            return comment;
          }
          return c;
        });

        return this.list;
      } catch (err: any) {
        debug(err);
        error('There was an error while adding a comment');
        return Promise.reject(err);
      }
    },
    async remove(id: string) {
      const { error } = useToast();
      try {
        const response = await Api.item.comments.remove(id);
        this.list = this.list.filter(comment => comment.id !== response.data.comment.id);
        return this.list;
      } catch (err: any) {
        debug(err);

        if (err.message) {
          for (const message of err.message) {
            error(message);
          }
        }
      }
    },
    getMentionsFromMessage(message: string) {
      const mentions: CommentMention[] = [];
      const loadedMentions = this.mentionLoaded;

      loadedMentions.forEach(mention => {
        if (message.includes(`@${mention.name}`)) {
          mentions.push(mention);
        }
      });

      this.mentionLoaded = [];
      return mentions;
    },
    searchMentionUsers(query: string) {
      const isLoggedIn = useUserStore().isLoggedIn;

      if (query.length < 2 || !isLoggedIn) {
        return Promise.resolve([]);
      }

      // EACH SEARCH SHOULD HAVE INDEPENDENT LIST USED FOR MENTIONING
      return Api.user.searchUsers(query).then(response => {
        const list: CommentMention[] = response.data?.results?.attributes?.users || [];

        list.forEach(user => {
          if (this.mentionLoaded.find(u => u.id === user.id)) {
            return;
          }

          this.mentionLoaded.push(user);
        });

        return Promise.resolve(list);
      });
    },
  },
  getters: {
    isCommentEdited: (state: CommentState) => (id: string) => {
      const comment = state.list.find(comment => comment.id === id);
      return comment?.createdAt !== comment?.updatedAt;
    },
  },
});
